import {
  Box,
  DialogTitle,
  Drawer,
  IconButton,
  Snackbar,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { ClosableDialog } from 'components/controls/dialogs'
import { getStockToExportRows } from 'components/domain/csv'
import { CloseIcon, CustomTooltip, HelpButton } from 'components/facets'
import { useExpansionData } from 'components/providers/CardDataProvider'
import { useUser } from 'components/providers/UserProvider'
import { linkArticlesToPurchaseEvent, recordUsage } from 'lib/api'
import { useAppContext, useDialog, useWindowDimensions } from 'lib/hooks'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import {
  ChangesContentInterface,
  ChangesInterface,
  useLocationStore,
} from 'lib/hooks/useLocationStore'
import { useMobileSidebarStore } from 'lib/hooks/useMobileSidebarStore'
import { FetchedStockInterface, useStockStore } from 'lib/hooks/useStockStore'
import { getReservedQuantityForIdentity, getStockedQuantityForIdentity } from 'lib/reservedQuantity'
import { areArticlesMatching, exposeToCypress, getArticleQty, getProperty } from 'lib/utils'
import _ from 'lodash'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import qs from 'query-string'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import {
  Activity,
  AnyYesNo,
  CardmarketCondition,
  ConditionEnum,
  Filters,
  generateArticleKey,
  getArticleIdentityKey,
  getFilteredStockStatistics,
  getStockMapFromStock,
  groupByPtArticle,
  InventoryArticle,
  INVENTORY_MULTI_SELECTION_UPDATES,
  INVENTORY_ZOOM_LEVELS,
  isSingle,
  LinkArticleToPurchaseEvent,
  Location,
  MkmProductInventoryMap,
  PlainRichArticle,
  PricedInventoryArticle,
  PurchaseEvent,
  roundTo2Decimals,
  STOCK_TYPES,
  TAXATION_ICON_VISIBILITY,
  TAXATION_SCHEME,
} from 'shared'
import { newCancellationToken } from 'utils/cancellationToken'
import {
  BACKOFFICE_USER_STOCK,
  ListingTabId,
  ListingTabsState,
  LISTING_AND_APPRAISAL,
  STOCK_DISPLAY,
} from 'utils/constants'
import { shallow } from 'zustand/shallow'
import { ExportAllProductsCsv } from '../csv/ExportAllProductsCsv'
import { ImportFileCsv } from '../csv/ImportFileCsv'
import { ActionsPresenter } from './ActionsPresenter'
import { applyFilters } from './applyFilters'
import { AutopricePresenter } from './AutopricePresenter'
import { CsvBulkEditDialog } from './CsvBulkEdit/CsvBulkEditDialog'
import { FilterDock } from './FilterDock'
import { LocationsPanel } from './LocationsPanel/LocationsPanel'
import { PriceSuggestPresenter } from './PriceSuggestPresenter/PriceSuggestPresenter'
import { PricingSettings } from './PricingSettings'
import { applySort, StockDisplay } from './StockDisplay'
import { ReservedQuantityReportDrawer } from './StockDisplay/ReservedQuantityReportDrawer'
import './StockDisplay/stock-display.css'
import { Sort } from './StockDisplay/StockDisplayUtils'
import { TabBox } from './TabBox'

export type DockAction = {
  icon: string
  actionLabel: string
  shouldConfirm?: boolean
  fn: () => void
  tooltip?: (fetchedAt: string) => JSX.Element
  key: string
}
export interface InventoryPresenterProps<Tab extends string> {
  listingTabsState?: ListingTabsState
  allListingStocks?: Record<number, FetchedStockInterface>
  onAddListingTab?: () => void
  onActivateListingTab?: (id: ListingTabId) => void
  onRenameListingTab?: (id: ListingTabId, title: string) => void
  onCloseListingTab?: (id: ListingTabId) => void
  onAssociateListingTabToLot?: (tabId: number, lot: PurchaseEvent) => void
  onUnlinkSelectedFromLot?: (selected: InventoryArticle[]) => void
  unlinkListingTabFromLot?: (tabId: number) => void
  moveSelectedItemsToTab?: (targetTabId: number) => void
  moveSelectedItemsToNewTab?: () => void
  moveAllStockToTab?: (targetTabId: number) => void
  purchaseEvents?: PurchaseEvent[]

  refreshStockAction?: DockAction
  extraDockActions?: DockAction[]
  allowAddingArticles?: boolean
  determineTab: (article: InventoryArticle) => Tab
  emptyStockText: string
  viewOnly?: boolean
  hideSettingsButton?: boolean
  hideExportButton?: boolean
  setStockState: (
    newValue: FetchedStockInterface | ((arg: FetchedStockInterface) => FetchedStockInterface)
  ) => void
  stockState: FetchedStockInterface
  openPublishDialog?: () => void
  parentPage: string
  disabledActions?: string[]
  showDialogForGradedCardsWithoutGrade?: boolean
  setShowDialogForGradedCardsWithoutGrade?: React.Dispatch<React.SetStateAction<boolean>>
  handleConfirmLinkToLotByListing?: (selectedLot: PurchaseEvent) => void
}

const mapThroughActiveTab = (
  fn: (article: InventoryArticle, index: number) => InventoryArticle,
  stock: InventoryArticle[],
  activeStock: InventoryArticle[]
): InventoryArticle[] => {
  let cnt = 0
  const newStock = stock.map((article, index) => {
    if (article.id !== activeStock[cnt]?.id) {
      return article
    } else {
      cnt++
    }
    return fn(article, index)
  })
  return newStock
}

export const InventoryPresenter = <Tab extends string>({
  listingTabsState,
  allListingStocks,
  onAddListingTab,
  onActivateListingTab,
  onRenameListingTab,
  onCloseListingTab,
  onAssociateListingTabToLot,
  unlinkListingTabFromLot,
  moveSelectedItemsToTab,
  moveSelectedItemsToNewTab,
  moveAllStockToTab,

  emptyStockText,
  allowAddingArticles = false,
  refreshStockAction,
  viewOnly = false,
  setStockState,
  stockState,
  hideExportButton,
  extraDockActions,
  openPublishDialog,
  parentPage,
  showDialogForGradedCardsWithoutGrade,
  setShowDialogForGradedCardsWithoutGrade,
  handleConfirmLinkToLotByListing: onConfirmLinkToLotByListing,
}: InventoryPresenterProps<Tab>): JSX.Element => {
  const { t } = useTranslation()
  const { isMobile, isPhone } = useAppContext()
  const { user, activeGame } = useUser()
  const isBelowXl = useMediaQuery('(max-width: 1199.98px)')
  const expansions = useExpansionData()
  const { width } = useWindowDimensions()
  const { enqueueSnackbar } = useSnackbar()
  const isFirstRender = useRef(true)
  const location = useLocation()

  // INVENTORY
  const [zoomLevel, sort, setSort, setDefaultTaxationScheme, setDisplayTaxationIcon] =
    useInventoryStore(
      (s) => [s.zoomLevel, s.sort, s.setSort, s.setDefaultTaxationScheme, s.setDisplayTaxationIcon],
      shallow
    )

  // LOCATION
  const [setChanges, setCurrentValues, setNewRows] = useLocationStore(
    (s) => [s.setChanges, s.setCurrentValues, s.setNewRows],
    shallow
  )

  // STOCK
  const [activeStockType, refreshedStock, setRefreshedStock, stockLock] = useStockStore(
    (s) => [s.activeStockType, s.refreshedStock, s.setRefreshedStock, s.stockLock],
    shallow
  )

  const [openCsvBulkEditDialog, setOpenCsvBulkEditDialog] = useState(false)
  const [setExportAllProductsCsvDialogOpen, ExportAllProductsCsvDialog] = useDialog()

  const [autopriceDialogOpen, setAutopriceDialogOpen] = useState(false)
  const { filterDock: openMobileFilterDock, setFilterDock: setOpenMobileFilterDock } =
    useMobileSidebarStore()
  const [currentArticle, setCurrentArticle] = useState<InventoryArticle | undefined>()
  // const [currentStock, setCurrentStock] = useState<InventoryArticle[]>([])
  const [filtersKey, setFiltersKey] = useState('this is used to force refresh')
  const [importFileDialogOpen, setImportFileDialogOpen] = useState(false)
  const [selectedStockValue, setSelectedStockValue] = useState(0)
  const [showLocationsPanel, setShowLocationsPanel] = useState(false)
  const [pricingSettingsDialogOpen, setPricingSettingsDialogOpen] = useState(false)

  // initial and working Current stock are used to render changes in the inventory view.
  // the two data structures are indexed by CM product id
  const [initialCurrentStock, setInitialCurrentStock] = useState<MkmProductInventoryMap>({})
  const [workingCurrentStock, setWorkingCurrentStock] = useState<MkmProductInventoryMap>({})

  const cancellationToken = newCancellationToken()

  const defaultFilters: Filters = useMemo(
    () => ({
      name: '',
      comment: '',
      location: '',
      edited: AnyYesNo.Any,
      priceChanged: AnyYesNo.Any,
      priceUnchanged: AnyYesNo.Any,
      priceFailed: AnyYesNo.Any,
      condition: [0, 6],
      rarity: [1, Object.keys(activeGame.rarities).length],
      minQuantity: null,
      maxQuantity: null,
      minPrice: null,
      maxPrice: null,
      playset: AnyYesNo.Any,
      failedAutopricing: AnyYesNo.Any,
      language: AnyYesNo.Any,
      selected: AnyYesNo.Any,
      firstEd: AnyYesNo.Any,
      signed: AnyYesNo.Any,
      bulk: AnyYesNo.Any,
      exactPrice: null,
      expansion: null,
      finishType: AnyYesNo.Any,
      graded: AnyYesNo.Any,
      american: AnyYesNo.Any,
      sold: AnyYesNo.Any,
    }),
    [activeGame]
  )

  const [filters, setFilters] = useState(defaultFilters)

  const areFiltersTouched = !(JSON.stringify(filters) === JSON.stringify(defaultFilters))

  const [priceSuggestDrawer, setPriceSuggestDrawer] = useState<{
    open: boolean
    article: InventoryArticle | null
    activeView?: 'buyPrice' | 'sellPrice'
    totalQuantity?: number
  }>({
    open: false,
    article: null,
    activeView: 'sellPrice',
    totalQuantity: 0,
  })

  const [reservedQuantityDrawer, setReservedQuantityDrawer] = useState<{
    open: boolean
    article: InventoryArticle | null
  }>({
    open: false,
    article: null,
  })

  const baseStock =
    activeStockType === STOCK_TYPES.SINGLES ? stockState.stock : stockState.sealedStock

  // sorted readonly
  const currentStock = useMemo(() => applySort(baseStock, sort), [baseStock, sort])

  const setStock = (
    next: ((prev: InventoryArticle[], stockType: string) => InventoryArticle[]) | InventoryArticle[]
  ) => {
    setStockState((prev) => {
      const prevArr = activeStockType === STOCK_TYPES.SINGLES ? prev.stock : prev.sealedStock
      const nextArr = typeof next === 'function' ? next(prevArr, activeStockType) : next
      return activeStockType === STOCK_TYPES.SINGLES
        ? { ...prev, stock: nextArr }
        : { ...prev, sealedStock: nextArr }
    })
  }

  const { stock, sealedStock, fetchedAt } = stockState

  const selectedStock = useMemo(() => {
    if (activeStockType === STOCK_TYPES.SINGLES) {
      return stock.filter((stockArticle) => stockArticle.selected)
    } else {
      return sealedStock.filter((stockArticle) => stockArticle.selected)
    }
  }, [stock, sealedStock, activeStockType])

  const editedStock = useMemo(() => {
    if (activeStockType === STOCK_TYPES.SINGLES) {
      return stock.filter(
        (stockArticle) => stockArticle.hasEditedAttribute() || stockArticle.hasEditedLocations()
      )
    } else {
      return sealedStock.filter((stockArticle) => stockArticle.hasEditedAttribute())
    }
  }, [stock, sealedStock, activeStockType])

  const handleChangeZoomLevel = () => {
    // if newZoomLevel = 'ptArticle' then we need to change the stock data to be grouped by location
    // if newZoomLevel = 'location' then we need to change the stock data to be exploded for every location
    const newStockData: any[] = []
    if (zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE) {
      const newStockMap: {
        [key: string]: InventoryArticle
      } = {}
      currentStock.forEach((article) => {
        const newArticle = article.copyWithNewId()
        newArticle.selected = false

        const key = generateArticleKey(newArticle)
        if (!newStockMap[key]) {
          newStockMap[key] = newArticle
          newStockMap[key].locations = newArticle.locations
        } else {
          newStockMap[key].quantity += newArticle.quantity
          newStockMap[key].locations?.push(...(newArticle.locations || []))
        }
      })

      // convert newStockMap to array
      newStockData.push(...Object.values(newStockMap))
    } else {
      currentStock.forEach((article) => {
        article.locations?.forEach((location) => {
          const newArticle = article.copyWithNewId()
          if (article.locations?.length === 1) {
            newArticle.quantity = article.quantity
            if (article.newQuantity) newArticle.newQuantity = article.newQuantity
          } else {
            newArticle.quantity = location.quantity
          }
          newArticle.locations = [location]
          newArticle.location = newArticle.locations[0].name
          newArticle.selected = false
          newStockData.push(newArticle)
        })
      })
    }

    initializeInitialAndWorkingStock(newStockData)
    setStock(newStockData)
  }

  const initializeInitialAndWorkingStock = (stock: InventoryArticle[]) => {
    // set initial stock and working stock to the current stock state
    const currentStockMap = getStockMapFromStock(stock)

    // it is necessary to deep clone the currentStockMap because otherwise the contents of initialCurrentStock
    // and workingCurrentStock would be pointing to the same object, and the changes would be reflected in both
    setInitialCurrentStock(_.cloneDeep(currentStockMap))
    setWorkingCurrentStock(_.cloneDeep(currentStockMap))
  }

  useEffect(() => {
    const { importCsv, settingsTab } = qs.parse(location.search)
    if (isFirstRender.current) {
      if (importCsv) setImportFileDialogOpen(true)
      else if (settingsTab) setPricingSettingsDialogOpen(true)
      isFirstRender.current = false
    }
    setDefaultTaxationScheme(
      activeStockType === STOCK_TYPES.SINGLES
        ? user.defaultTaxationSchema?.singles
        : user.defaultTaxationSchema?.sealed
    )
    setDisplayTaxationIcon(
      user.defaultTaxationSchema?.taxationIconVisibility || TAXATION_ICON_VISIBILITY.ONLY_DIFFERENT
    )

    const sortData = localStorage.getItem('sortArticles')
    if (sortData) {
      const parsedSort = JSON.parse(sortData) as Sort

      if (parsedSort.by !== 'listedAt') setSort(JSON.parse(sortData) as Sort)
      else
        setSort({
          by: 'card.name',
          direction: 'asc',
          type: 'string',
        })
    } else
      setSort({
        by: 'card.name',
        direction: 'asc',
        type: 'string',
      })
  }, [])

  useEffect(() => {
    // Only this sets `location` on each row, which is what the card shows.
    if ([STOCK_DISPLAY, BACKOFFICE_USER_STOCK].includes(parentPage)) {
      handleChangeZoomLevel()
      setRefreshedStock(false)
    }
  }, [zoomLevel, refreshedStock])

  useEffect(() => {
    let sum = 0
    selectedStock.forEach((article) => {
      if (article.newPrice) {
        sum += article.newPrice * article.quantity
      } else {
        sum += article.price * article.quantity
      }
    })
    setSelectedStockValue(roundTo2Decimals(sum))
  }, [selectedStock])

  useEffect(() => {
    if (parentPage === STOCK_DISPLAY) computeNewStockToDisplay()
  }, [workingCurrentStock])

  exposeToCypress({
    setArticles: (articles: PlainRichArticle[]) =>
      setStock(articles.map((article) => new InventoryArticle(article))),
    clearArticles: () => setStock([]),
  })

  const openPriceSuggestDrawer = useCallback(
    (article: InventoryArticle, activeView?: 'buyPrice' | 'sellPrice'): void => {
      let totalQuantity = 0
      if (parentPage === STOCK_DISPLAY) {
        const completeArticle = groupByPtArticle(currentStock).get(article.ptArticleId!) || article
        totalQuantity = completeArticle.quantity
      }

      setPriceSuggestDrawer({ open: true, article, activeView, totalQuantity })
    },
    [setPriceSuggestDrawer]
  )

  const openReservedQuantityDrawer = useCallback(
    (article: InventoryArticle): void => {
      setReservedQuantityDrawer({ open: true, article })
    },
    [setReservedQuantityDrawer]
  )

  const closePriceDrawer = useCallback(() => {
    setPriceSuggestDrawer((s) => ({ ...s, open: false }))
  }, [])
  const closeReservedDrawer = useCallback(
    () => setReservedQuantityDrawer((s) => ({ ...s, open: false })),
    []
  )

  const priceArticle = (price: number): void => {
    if (price > 0) {
      const target = priceSuggestDrawer.article
      const newStock = currentStock.map((article) => {
        if (article.id === target?.id) {
          return new InventoryArticle({
            ...article,
            failedAutopricing: false,
            newPrice: price,
          })
        } else return article
      })
      setStock(newStock)

      // In STOCK_DISPLAY the displayed stock is recomputed from workingCurrentStock on every
      // workingCurrentStock change (computeNewStockToDisplay). If we only set newPrice on the
      // display array, a later recompute (e.g. confirming the locations panel on another article)
      // rebuilds the list from working/initial stock and silently drops this price edit.
      // So persist the price into workingCurrentStock too, mirroring handleConfirmBulkEdit.
      if (parentPage === STOCK_DISPLAY && target) {
        const newWorkingCurrentStock = _.cloneDeep(workingCurrentStock)
        const bucket = newWorkingCurrentStock[target.card._id]
        const index = bucket?.findIndex((article) => article.id === target.id) ?? -1
        if (bucket && index !== -1) {
          // Rebuild a plain InventoryArticle (mirrors the setStock branch above) so the manual
          // price drops any autopricing metadata: a bare mutation would keep a
          // PricedInventoryArticle and the stale strategy tooltip would resurface when the grid is
          // recomputed from workingCurrentStock.
          bucket[index] = new InventoryArticle({
            ...bucket[index],
            failedAutopricing: false,
            newPrice: price,
          })
          setWorkingCurrentStock(newWorkingCurrentStock)
        }
      }
    }
    setPriceSuggestDrawer({ open: false, article: null })
  }

  const handleDeleteArticle = useCallback(
    (target: InventoryArticle | 'selected' | 'all') => {
      setStock((prev) => {
        if (target === 'selected') return prev.filter((a) => !a.selected)
        if (target === 'all') return []
        return prev.filter((a) => a.id !== target.id)
      })
    },
    [setStock]
  )

  const sortInventoryArticles = (articles: InventoryArticle[]) => {
    const finalStock = [...articles]
    return finalStock.sort((a, b) => {
      const firstItem = getProperty(sort.by, a)
      const secondItem = getProperty(sort.by, b)

      if (sort.type === 'string' && sort.by !== 'card.name') {
        let firstString
        let secondString
        if (sort.by === 'locationName') {
          firstString = a.locations![0].name || ''
          secondString = b.locations![0].name || ''
        } else {
          firstString = String(getProperty(sort.by, a) || '')
            .trim()
            .toLowerCase()
          secondString = String(getProperty(sort.by, b) || '')
            .trim()
            .toLowerCase()
        }

        const comparison = firstString.localeCompare(secondString)
        if (comparison !== 0) return sort.direction === 'asc' ? comparison : -comparison
      } else if (sort.type === 'number') {
        let firstNumber = Number(firstItem)
        let secondNumber = Number(secondItem)

        if (sort.by === 'price') {
          firstNumber = Number(getProperty('price', a) || firstNumber)
          secondNumber = Number(getProperty('price', b) || secondNumber)
        }

        if (firstNumber !== secondNumber) {
          return sort.direction === 'asc' ? firstNumber - secondNumber : secondNumber - firstNumber
        }
      }

      const nameComparison = a.card.name
        .trim()
        .toLowerCase()
        .localeCompare(b.card.name.trim().toLowerCase())
      if (nameComparison !== 0) return nameComparison

      if (ConditionEnum[a.condition] > ConditionEnum[b.condition]) return 1
      if (ConditionEnum[a.condition] < ConditionEnum[b.condition]) return -1

      if (a.idLanguage > b.idLanguage) return 1
      if (a.idLanguage < b.idLanguage) return -1

      if (a.locations && b.locations && a.locations[0].name > b.locations[0].name) {
        return 1
      }
      if (a.locations && b.locations && a.locations[0].name < b.locations[0].name) {
        return -1
      }

      if (a.countryEdition > b.countryEdition) return 1
      if (a.countryEdition < b.countryEdition) return -1

      // first items without grading company
      if (a.gradingCompany === undefined && b.gradingCompany !== undefined) return -1
      if (a.gradingCompany !== undefined && b.gradingCompany === undefined) return 1

      if (a.gradingCompany && b.gradingCompany) {
        if (a.gradingCompany > b.gradingCompany) return 1
        if (a.gradingCompany < b.gradingCompany) return -1
      }

      return 0
    })
  }

  const clearFailedAutoprice = useCallback(
    (target: 'selected' | 'all'): void => {
      const newStock = mapThroughActiveTab(
        (item) => {
          if (target === 'all' || (target === 'selected' && item.selected)) {
            const { failedAutopricing, ...newArticle } = item
            return new InventoryArticle({ ...newArticle })
          } else return item
        },
        currentStock,
        stock
      )

      setStock(newStock)
    },
    [setStock, mapThroughActiveTab, stock]
  )

  const updateCommentOnMultiSelection = (value: any, newWorkingCurrentStock: any) => {
    const commentFor = (article: InventoryArticle): string =>
      typeof value === 'function' ? value(article) : value

    if (parentPage === LISTING_AND_APPRAISAL) {
      setStock((oldStock) => {
        const selected = oldStock.filter((a) => a.selected)

        if (selected.length === 0 || selected.every((a) => a.comment === commentFor(a))) {
          return oldStock
        }

        return oldStock.map((article) => {
          const mustUpdate =
            article.selected || selected.some((sel) => areArticlesMatching(article, sel))
          const newComment = commentFor(article)

          if (mustUpdate && article.comment !== newComment) {
            const copy = article.copy()
            copy.comment = newComment
            copy.selected = true
            return copy
          }
          return article
        })
      })
    } else {
      const selectedArticleIdsSet = new Set<number>(
        currentStock
          .filter(
            (article) =>
              article.selected &&
              article.comment !== commentFor(article) &&
              article.newQuantity !== 0
          )
          .map((article) => Number(article.ptProductId))
      )

      if (selectedArticleIdsSet.size === 0) return

      Object.keys(workingCurrentStock).forEach((cardmarketId) => {
        newWorkingCurrentStock[cardmarketId].forEach((article: InventoryArticle) => {
          // don't need to search matching article because I'm updating all articles with same ptProductId
          if (selectedArticleIdsSet.has(Number(article.ptProductId!))) {
            article.newComment = commentFor(article)
            article.selected = true
          }
        })
      })

      setWorkingCurrentStock(newWorkingCurrentStock)
    }
  }

  const updateQuantityOnMultiSelection = (value: any, newWorkingCurrentStock: any) => {
    // Running total per article identity — updated as each article is processed so that
    // subsequent articles in the same identity bucket see the already-adjusted quantity.
    // Identity (rather than ptArticleId) is the right key because location-panel splits
    // stage articles that share ptArticleId with their source despite being different
    // SKUs.
    const mapIdentityKeyTotalQty = new Map<string, number>()
    let displayWarning = false
    let displayGradedWarning = false

    currentStock.forEach((article) => {
      if (!article.selected) return
      if ((value as number) === article.attributes[0]) return

      if (article.graded.isGraded) {
        displayGradedWarning = true
        return
      }

      const newQty = typeof value === 'function' ? Number(value(article)) : Number(value)
      // No quantity in inventory can go below zero — reject the bulk for this article.
      if (!Number.isFinite(newQty) || newQty < 0) {
        displayWarning = true
        return
      }

      const newArticle = article.copy()
      const oldQty = Number(article.quantity) // saved baseline — used by location.deltaQuantity
      // The reserved-quantity check has to operate on the *staged* total of this
      // article, not its saved baseline. Otherwise a panel-staged cell (visible
      // `oldQty → stagedQty`) under-counts what the bulk would actually remove and
      // sails past the reserved threshold.
      const stagedQty = getArticleQty(article)

      if (article.ptArticleId && article.ptArticleReservedQuantity) {
        const identityKey = getArticleIdentityKey(article)
        const totalBefore =
          mapIdentityKeyTotalQty.get(identityKey) ??
          getStockedQuantityForIdentity(
            workingCurrentStock,
            article.card._id as number,
            identityKey
          )

        const totalAfter = totalBefore - stagedQty + newQty

        if (totalAfter < Number(article.ptArticleReservedQuantity)) {
          displayWarning = true
          return
        }

        let matched = false
        newArticle.locations?.forEach((location) => {
          if (location.name === article.location) {
            matched = true
            location.quantity = newQty
            location.deltaQuantity = newQty - oldQty
          }
        })

        if (matched) {
          mapIdentityKeyTotalQty.set(identityKey, totalAfter)
        } else {
          return
        }
      } else {
        newArticle.locations?.forEach((location) => {
          if (location.name === article.location) {
            location.quantity = newQty
            location.deltaQuantity = newQty - oldQty
          }
        })
      }

      const cardmarketProductId = newArticle.card._id as number
      const index = newWorkingCurrentStock[cardmarketProductId].findIndex((a: InventoryArticle) => {
        return a.attributes[9] === newArticle.attributes[9]
      })
      newWorkingCurrentStock[cardmarketProductId][index] = newArticle
    })

    if (displayWarning)
      enqueueSnackbar(t('stockPricing.reservedQuantityWarning'), { variant: 'warning' })

    if (displayGradedWarning)
      enqueueSnackbar(t('stockPricing.gradedQuantityWarning'), {
        variant: 'warning',
        autoHideDuration: 20000,
      })

    setWorkingCurrentStock(newWorkingCurrentStock)
  }

  const updateConditionOnMultiSelection = (
    value: any,
    copyOfCurrentStock: InventoryArticle[],
    newWorkingCurrentStock: any
  ) => {
    if (parentPage === LISTING_AND_APPRAISAL) {
      copyOfCurrentStock.forEach((article) => {
        if (article.selected) article.condition = value as CardmarketCondition
      })
      setStock(copyOfCurrentStock)
    } else {
      // Running total per article identity — updated as each article is processed. See
      // the matching comment in updateQuantityOnMultiSelection for why we key by identity
      // instead of ptArticleId.
      const mapIdentityKeyTotalQty = new Map<string, number>()
      let displayWarning = false
      currentStock.forEach((article) => {
        if (article.selected) {
          if (
            (value as CardmarketCondition) !== article.attributes[1] &&
            article.newQuantity !== 0
          ) {
            if (article.newCondition === undefined) {
              const copyOfNewArticle = article.copyWithNewId()
              const newArticle = _.cloneDeep(copyOfNewArticle)

              if (article.ptArticleId && article.ptArticleReservedQuantity) {
                const identityKey = getArticleIdentityKey(article)
                const totalBefore =
                  mapIdentityKeyTotalQty.get(identityKey) ??
                  getStockedQuantityForIdentity(
                    workingCurrentStock,
                    article.card._id as number,
                    identityKey
                  )
                // A condition change moves the article's whole *staged* quantity into
                // the new SKU, so the amount removed from this identity is the visible
                // staged total, not the saved baseline.
                const stagedQty = getArticleQty(article)
                const totalAfter = totalBefore - stagedQty

                if (totalAfter < Number(article.ptArticleReservedQuantity)) {
                  displayWarning = true
                  return
                }

                mapIdentityKeyTotalQty.set(identityKey, totalAfter)
              }

              newArticle.newCondition = value as CardmarketCondition
              newArticle.condition = value as CardmarketCondition
              // The condition change re-targets this article at the pt_article matching
              // the new SKU. Inherit that pt_article's real reserved quantity if it
              // exists in the inventory; otherwise the new SKU has no DB row yet and
              // reserved is 0. Either way, the source's reserved (carried over by
              // `_.cloneDeep`) is wrong — never use it.
              newArticle.ptArticleReservedQuantity = getReservedQuantityForIdentity(
                workingCurrentStock,
                article.card._id as number,
                getArticleIdentityKey(newArticle)
              )
              newArticle.locations?.forEach((location) => {
                if (location.name === article.location) {
                  location.deltaQuantity = location.quantity
                }
              })

              //now I have to look for the starting object (article) and set the quantity and the respective location to 0
              const cardmarketProductId = article.card._id as number

              //index of the existing article which must be set to 0
              const index = newWorkingCurrentStock[cardmarketProductId].findIndex(
                (oldArticle: InventoryArticle) => {
                  return oldArticle.attributes[9] === article.attributes[9]
                }
              )
              if (index !== -1) {
                newWorkingCurrentStock[cardmarketProductId][index].attributes[0] = 0 //quantity
                newWorkingCurrentStock[cardmarketProductId][index].locations![0].quantity = 0 //location quantity
                newWorkingCurrentStock[cardmarketProductId][index].locations![0].deltaQuantity =
                  -article.quantity
              }

              // add new article in list of articles in newWorkingCurrentStock
              newWorkingCurrentStock[cardmarketProductId].push(newArticle)
            } else {
              article.newCondition = value as CardmarketCondition
              article.condition = value as CardmarketCondition
              const cardmarketProductId = article.card._id as number
              const index = newWorkingCurrentStock[cardmarketProductId].findIndex(
                (oldArticle: InventoryArticle) => {
                  return oldArticle.attributes[9] === article.attributes[9]
                }
              )
              newWorkingCurrentStock[cardmarketProductId][index] = article
            }
          }
        }
      })

      if (displayWarning) {
        enqueueSnackbar(t('stockPricing.reservedQuantityWarning'), { variant: 'warning' })
      }
      setWorkingCurrentStock(newWorkingCurrentStock)
    }
  }

  const updateLocationOnMultiSelection = (
    value: any,
    copyOfCurrentStock: InventoryArticle[],
    newWorkingCurrentStock: any
  ) => {
    if (parentPage === LISTING_AND_APPRAISAL) {
      copyOfCurrentStock.forEach((article) => {
        if (article.selected) {
          article.location = value as string
          article.locations! = [
            {
              _id: '',
              userId: '',
              ptArticleId: 0,
              name: value as string,
              quantity: article.quantity,
              deltaQuantity: article.quantity,
              totalPickedQuantity: 0,
              totalQuantityWithIssues: 0,
            },
          ]
        }
      })
      setStock(copyOfCurrentStock)
    } else {
      currentStock.forEach((article) => {
        if (article.selected) {
          if ((value as string) !== article.location && article.newQuantity !== 0) {
            // if(article.newQuantity === undefined){
            // to work on the location I have to bring the old card-location to 0 and add a new item in the locations list
            const copyOfNewArticle = article.copyWithNewId()
            const newArticle = _.cloneDeep(copyOfNewArticle)

            //update values of new article according new location
            newArticle.location = value as string

            newArticle.locations?.forEach((location) => {
              if (location.name === article.location) {
                location.name = value as string
                location.deltaQuantity = location.quantity
              }
            })

            //now I have to look for the starting object (article) and set the quantity and the respective location to 0
            const cardmarketProductId = article.card._id as number

            //index of the existing article which must be set to 0
            const index = newWorkingCurrentStock[cardmarketProductId].findIndex(
              (oldArticle: InventoryArticle) => {
                return oldArticle.attributes[9] === article.attributes[9]
              }
            )
            if (index !== -1) {
              // if you are creating a new card for the first time, then reset the starting card and insert the new item
              if (article.newQuantity === undefined) {
                newWorkingCurrentStock[cardmarketProductId][index].attributes[0] = 0 //quantity
                newWorkingCurrentStock[cardmarketProductId][index].locations![0].quantity = 0 //location quantity
                newWorkingCurrentStock[cardmarketProductId][index].locations![0].deltaQuantity =
                  -article.quantity
                // add new article in list of articles in newWorkingCurrentStock
                newWorkingCurrentStock[cardmarketProductId].push(newArticle)
              } else {
                /* otherwise remove the existing one and insert the new created article. 
                this is because otherwise the computeNewStockToDisplay makes incorrect calculations and remains
                trace of the intermediate modification  */
                newWorkingCurrentStock[cardmarketProductId].splice(index, 1)
                newWorkingCurrentStock[cardmarketProductId].push(newArticle)
              }
            }
          }
        }
      })
      setWorkingCurrentStock(newWorkingCurrentStock)
    }
  }

  const editBulkBuyPrice = (value: any): { skipped: number } => {
    const selectedArticles = currentStock.filter((article) => {
      return article.selected
    })

    // Count articles that the callback decides to skip (returns undefined/null).
    // Done synchronously here because setStock runs asynchronously.
    let skipped = 0
    if (typeof value === 'function') {
      for (const article of selectedArticles) {
        const newVal = value(article)
        if (newVal === undefined || newVal === null) skipped++
      }
    }

    setStock((oldStock) =>
      oldStock.map((article) => {
        const matching = selectedArticles.find((selected) => {
          return areArticlesMatching(selected, article)
        })

        if (matching) {
          const newValue = typeof value === 'function' ? value(article) : value

          if (newValue === undefined || newValue === null) return article

          const oldValue = article.buyPrice

          if (newValue !== oldValue) {
            const newArticle = article.copy()
            newArticle.buyPrice = newValue
            newArticle.buyPriceLock = true
            newArticle.selected = true
            newArticle.failedAutopricing = false
            return newArticle
          }
        }

        return article
      })
    )

    return { skipped }
  }

  const updatePriceOnMultiSelection = (
    value: any,
    newWorkingCurrentStock: MkmProductInventoryMap
  ) => {
    if (parentPage === LISTING_AND_APPRAISAL) {
      const selectedArticles = currentStock.filter((article) => {
        return article.selected
      })
      setStock((oldStock) =>
        oldStock.map((article) => {
          const matching = selectedArticles.find((selected) => {
            return areArticlesMatching(selected, article)
          })

          if (matching) {
            const newValue = typeof value === 'function' ? value(article) : value
            const oldValue = article.newPrice ?? article.price

            if (newValue !== oldValue) {
              // Build a plain InventoryArticle so a manually set price drops any autopricing
              // metadata (priceReport): otherwise a previously autopriced article keeps showing
              // the stale strategy tooltip on hover. Mirrors `priceArticle`.
              const newArticle = new InventoryArticle({
                ...article,
                failedAutopricing: false,
                newPrice: newValue,
              })
              newArticle.selected = true
              return newArticle
            }
          }

          return article
        })
      )
    } else {
      const selectedArticleIdsSet = new Set<number>(
        currentStock
          .filter(
            (article) => article.selected && article.comment !== value && article.newQuantity !== 0
          )
          .map((article) => Number(article.ptProductId))
      )

      if (selectedArticleIdsSet.size === 0) return

      Object.keys(workingCurrentStock).forEach((cardmarketId: any) => {
        const cardmarketArticles = newWorkingCurrentStock[cardmarketId]
        cardmarketArticles.forEach((article: InventoryArticle, index: number) => {
          // don't need to search matching article because I'm updating all articles with same ptProductId
          if (selectedArticleIdsSet.has(Number(article.ptProductId!))) {
            const newValue = typeof value === 'function' ? value(article) : value
            if (newValue !== article.price) {
              if (article instanceof PricedInventoryArticle) {
                // A manually set price isn't from a strategy: rebuild a plain InventoryArticle so the
                // stale autopricing tooltip (priceReport) doesn't resurface once the grid is
                // recomputed from workingCurrentStock. Only autopriced rows pay this allocation.
                const newArticle = new InventoryArticle({
                  ...article,
                  failedAutopricing: false,
                  newPrice: newValue,
                })
                newArticle.selected = true
                cardmarketArticles[index] = newArticle
              } else {
                article.newPrice = newValue
                article.selected = true
              }
            }
          }
        })
        setWorkingCurrentStock(newWorkingCurrentStock)
      })
    }
  }

  const updateTaxationSchemeOnMultiSelection = (
    value: TAXATION_SCHEME,
    newWorkingCurrentStock: MkmProductInventoryMap
  ) => {
    if (parentPage === LISTING_AND_APPRAISAL) {
      setStock((oldStock) => {
        return oldStock.map((article) => {
          if (value === TAXATION_SCHEME.MARGIN) {
            const mustUpdate = article.selected && article.marginTaxationQuantity === 0

            if (mustUpdate) {
              const copy = article.copy()
              copy.marginTaxationQuantity = copy.quantity
              copy.standardTaxationQuantity = 0
              copy.taxationScheme = TAXATION_SCHEME.MARGIN
              return copy
            }
          }
          if (value === TAXATION_SCHEME.STANDARD) {
            const mustUpdate = article.selected && article.standardTaxationQuantity === 0

            if (mustUpdate) {
              const copy = article.copy()
              copy.standardTaxationQuantity = copy.quantity
              copy.marginTaxationQuantity = 0
              copy.taxationScheme = TAXATION_SCHEME.STANDARD

              return copy
            }
          }
          return article
        })
      })
    } else {
      const selectedArticleIdsSet = new Set<number>(
        currentStock
          .filter((article) => article.selected)
          .map((article) => Number(article.ptProductId))
      )

      if (selectedArticleIdsSet.size === 0) return

      Object.keys(workingCurrentStock).forEach((cardmarketId: any) => {
        newWorkingCurrentStock[cardmarketId].forEach((article: InventoryArticle) => {
          // don't need to search matching article because I'm updating all articles with same ptProductId
          if (selectedArticleIdsSet.has(Number(article.ptProductId!))) {
            if (value === TAXATION_SCHEME.MARGIN) {
              article.newMarginTaxationQuantity = article.quantity
              article.newStandardTaxationQuantity = 0
            } else {
              article.newStandardTaxationQuantity = article.quantity
              article.newMarginTaxationQuantity = 0
            }
          }
        })
        setWorkingCurrentStock(newWorkingCurrentStock)
      })
    }
  }

  const updateAttributesOfMultiSelectionArticle = (
    name: INVENTORY_MULTI_SELECTION_UPDATES,
    value:
      | string
      | number
      | boolean
      | TAXATION_SCHEME
      | ((article: InventoryArticle) => string | number | boolean)
  ) => {
    const copyOfCurrentStock = _.cloneDeep(currentStock)
    const newWorkingCurrentStock: any = { ...workingCurrentStock }

    switch (name) {
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_COMMENT:
        updateCommentOnMultiSelection(value, newWorkingCurrentStock)
        break
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_QUANTITY:
        updateQuantityOnMultiSelection(value, newWorkingCurrentStock)
        break
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_CONDITION:
        updateConditionOnMultiSelection(value, copyOfCurrentStock, newWorkingCurrentStock)
        break
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_LOCATION:
        updateLocationOnMultiSelection(value, copyOfCurrentStock, newWorkingCurrentStock)
        break
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_PRICE:
        updatePriceOnMultiSelection(value, newWorkingCurrentStock)
        break
      case INVENTORY_MULTI_SELECTION_UPDATES.NEW_TAXATION_SCHEME:
        updateTaxationSchemeOnMultiSelection(value as TAXATION_SCHEME, newWorkingCurrentStock)
        break

      default:
        return
    }
  }

  const revertArticleEdit = (article: InventoryArticle | 'selected' | 'all'): void => {
    const shouldBeReverted = (item: InventoryArticle): boolean => {
      if (article === 'selected') return !!item.selected
      if (article === 'all') return true
      return item.id === article.id
    }

    if (parentPage === LISTING_AND_APPRAISAL) {
      setStock((oldStock) => {
        const emptyArticlesIndexes: number[] = []
        let newStock = oldStock.map((item) => {
          if (shouldBeReverted(item)) {
            const newArt = item.copy()
            newArt.revertChanges()
            return newArt
          } else return item
        })

        if (emptyArticlesIndexes.length > 0) {
          newStock = newStock.filter((_, index) => !emptyArticlesIndexes.includes(index))
        }

        return newStock
      })
    } else {
      // revert changes in the working stock
      const newWorkingCurrentStock = { ...workingCurrentStock }

      currentStock.forEach((item) => {
        if (shouldBeReverted(item)) {
          const cardmarketProductId = item.card._id
          const workingArticleIdx = initialCurrentStock[cardmarketProductId].findIndex(
            (article) => article.id === item.id
          )

          // update equivalent card in workingCurrentStock
          if (workingArticleIdx !== -1) {
            const articleToUse = initialCurrentStock[cardmarketProductId][workingArticleIdx]
            const copyOfArticle = _.cloneDeep(articleToUse)
            copyOfArticle.revertChanges()
            newWorkingCurrentStock[cardmarketProductId][workingArticleIdx] = copyOfArticle
          } else {
            // case in which this card is a new card generated due to an attribute change
            newWorkingCurrentStock[cardmarketProductId] = newWorkingCurrentStock[
              cardmarketProductId
            ].filter((articles) => articles.id !== item.id)
          }
        }
      })
      setWorkingCurrentStock(newWorkingCurrentStock)
    }
  }

  const showLocations = (article: InventoryArticle) => {
    setCurrentArticle(article)
    setShowLocationsPanel(true)
  }

  const [filteredStock, selectedArticlesHiddenByFilters] = useMemo(() => {
    const filteredStock = applyFilters(filters, currentStock, activeGame)

    let sortedStock = applySort(filteredStock, sort)

    if (parentPage === LISTING_AND_APPRAISAL) {
      sortedStock =
        activeStockType === STOCK_TYPES.SINGLES
          ? sortedStock.concat(sealedStock)
          : sortedStock.concat(stock)
    }

    const selectedFilteredStockLength = filteredStock.reduce(
      (acc, val) => acc + (val.selected ? 1 : 0),
      0
    )
    const selectedArticlesHiddenByFilters = selectedStock.length - selectedFilteredStockLength

    return [sortedStock, selectedArticlesHiddenByFilters]
  }, [filters, activeGame, currentStock])

  const lastVisibleIndexRef = useRef<number | null>(null)

  const selectArticle = useCallback(
    (article: InventoryArticle, shiftSelect = false) => {
      const clickedIdx = filteredStock.findIndex((a) => a.id === article.id)

      setStock((prev) => {
        // toggle del cliccato
        let next = prev.map((a) => {
          if (a.id !== article.id) return a
          const c = a.copy()
          c.toggleSelected()
          return c
        })

        if (shiftSelect && lastVisibleIndexRef.current != null && clickedIdx !== -1) {
          const [lo, hi] =
            lastVisibleIndexRef.current < clickedIdx
              ? [lastVisibleIndexRef.current, clickedIdx]
              : [clickedIdx, lastVisibleIndexRef.current]

          const rangeIds = new Set(filteredStock.slice(lo, hi + 1).map((a) => a.id))

          next = next.map((a) => {
            if (!rangeIds.has(a.id)) return a
            const c = a.copy()
            c.selected = true
            return c
          })
        }

        // update visible shift “caret” according visible list
        if (clickedIdx !== -1) lastVisibleIndexRef.current = clickedIdx
        return next
      })
    },
    [filteredStock, setStock]
  )

  const selectAllResults = useCallback(() => {
    const visibleIds = new Set(filteredStock.map((a) => a.id))
    setStock((prev) =>
      prev.map((a) => {
        if (!visibleIds.has(a.id)) return a
        const c = a.copy()
        c.selected = true
        return c
      })
    )
  }, [filteredStock, setStock])

  const unselectAll = useCallback((): void => {
    setStock((oldStock) =>
      oldStock.map((stockArticle) => {
        const newArt = stockArticle.copy()
        newArt.selected = false
        return newArt
      })
    )
  }, [setStock])

  const openImportFileDialog = useCallback(() => setImportFileDialogOpen(true), [])
  const exportSelectedCsv = useCallback(() => {
    if (!selectedStock.length) return

    recordUsage(Activity.ExportCSV, {
      count: selectedStock.length,
      idGame: activeGame.idGame,
    }).then(null)

    const rows = getStockToExportRows(selectedStock, expansions, activeGame).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    )

    if (!rows.length) return

    const headers = Object.keys(rows[0])
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        headers
          .map((h) => {
            const val = (row as unknown as Record<string, unknown>)[h]
            if (val === undefined || val === null) return ''
            const str = String(val)
            return str.includes(',') || str.includes('"') || str.includes('\n')
              ? `"${str.replace(/"/g, '""')}"`
              : str
          })
          .join(',')
      ),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `export-${user?.username}-${moment(Date.now()).format('DD-MM-YYYY_HH-mm')}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }, [selectedStock, activeGame, expansions, user?.username])

  const openExportAllProductsCsvDialog = useCallback(
    () => setExportAllProductsCsvDialogOpen(true),
    []
  )

  const handleAddArticles = (articles: InventoryArticle[]) => {
    const activeListingTabId = listingTabsState!.activeTabId
    const activeTab = listingTabsState!.tabs.find((tab) => tab.id === activeListingTabId)
    const lotId = activeTab?.lotId

    // If the tab is linked to a batch, we set the purchaseEventId on the items we are adding
    const articlesToAdd =
      lotId != null
        ? articles.map((article) => {
            const copy = article.copy()
            copy.purchaseEventId = lotId
            return copy
          })
        : articles

    setStock((prev) => prev.concat(articlesToAdd))
  }

  const openAutopriceDialog = useCallback(() => setAutopriceDialogOpen(true), [])

  const totalFilteredStockValues = useMemo(() => {
    const { price, newPrice, buyPrice, quantity, newQuantity, uniqueArticles } =
      getFilteredStockStatistics(filteredStock)
    return { price, newPrice, buyPrice, quantity, newQuantity, uniqueArticles }
  }, [filteredStock])

  // const boxStyle = useMemo(
  //   () => ({ backgroundColor: theme.palette.lightBlue, height: '100vh', overflow: 'hidden' }),
  //   []
  // )

  type Command =
    | 'selectAllResults'
    | 'revertArticleEdit'
    | 'updateAttributesOfMultiSelectionArticle'

  const commandMap: Record<Command, (...args: any[]) => void> = {
    selectAllResults,
    revertArticleEdit,
    updateAttributesOfMultiSelectionArticle,
  }

  const [command, setCommand] = useState<{ name: Command; args: any[] } | null>(null)

  useEffect(() => {
    if (command && commandMap[command.name]) {
      //@ts-ignore
      commandMap[command.name](...command.args)
      setCommand(null)
    }
  }, [command])

  const selectAllResultsCommand = useCallback(
    () => setCommand({ name: 'selectAllResults', args: [] }),
    [setCommand]
  )

  const revertArticleEditCommand = useCallback(
    (...args: Parameters<typeof revertArticleEdit>) =>
      setCommand({ name: 'revertArticleEdit', args }),
    [setCommand]
  )

  const updateAttributesOfMultiSelectionArticleCommand = useCallback(
    (...args: Parameters<typeof updateAttributesOfMultiSelectionArticle>) =>
      setCommand({ name: 'updateAttributesOfMultiSelectionArticle', args: args }),
    [setCommand]
  )

  const getNewQuantity = (quantity: number, deltaQuantity?: number) => {
    const newQuantity = quantity + (deltaQuantity || 0)
    return newQuantity
  }

  const prepareNewArticleWithNewValues = (
    newArticle: InventoryArticle,
    newValues: ChangesContentInterface,
    realLocationName: string
  ) => {
    // set new values to newArticle
    if (newValues.deltaQuantity) {
      if (newValues.isNewRow === true)
        newArticle.quantity = getNewQuantity(0, newValues.deltaQuantity)
      else newArticle.quantity = getNewQuantity(newValues.quantity, newValues.deltaQuantity)
    }

    //case in which I am selecting X cards of Y and I am changing its attribute or location
    else if (!newValues.deltaQuantity && newValues.quantity)
      newArticle.quantity = newValues.quantity

    if (newValues.condition) {
      newArticle.condition = newValues.condition as any
    }
    if (newValues.idLanguage) {
      newArticle.idLanguage = newValues.idLanguage
    }
    if (newValues.locationName !== undefined) {
      newArticle.location = newValues.locationName
      if (newValues.isNewRow === true)
        newArticle.locations! = [
          {
            _id: '',
            userId: '',
            ptArticleId: 0,
            name: newValues.locationName,
            quantity: newArticle.quantity,
            deltaQuantity: newValues.deltaQuantity ?? newArticle.quantity,
            totalPickedQuantity: 0,
            totalQuantityWithIssues: 0,
          },
        ]
    }
    if (newValues.finishType) {
      newArticle.finishType = newValues.finishType
    }
    if (newValues.countryEdition) {
      newArticle.countryEdition = newValues.countryEdition
    }
    if (newValues.isSigned !== undefined) {
      newArticle.isSigned = newValues.isSigned
    }
    if (newValues.isFirstEd !== undefined) {
      newArticle.isFirstEd = newValues.isFirstEd
    }

    if (newValues.gradingCompany !== undefined) {
      newArticle.gradingCompany = newValues.gradingCompany
    }
    if (newValues.grade !== undefined) {
      newArticle.grade = newValues.grade
    }

    if (!newValues.isNewRow) {
      // update locations for newArticle
      newArticle.locations = newArticle.locations ?? []
      const locationIndex = newArticle.locations.findIndex((loc) => {
        return loc.name === (newValues.locationName ?? realLocationName)
      })

      // if there is deltaQ it means that I am modifying the quantity of a card
      if (locationIndex !== -1 && newValues.deltaQuantity) {
        const newLocations = [...newArticle.locations]
        if (!newLocations[locationIndex].deltaQuantity)
          newLocations[locationIndex].deltaQuantity = 0
        newLocations[locationIndex] = {
          ...newArticle.locations[locationIndex],
          name: newValues.locationName ?? realLocationName,
          quantity: newArticle.quantity,
          //@ts-ignore
          deltaQuantity: newLocations[locationIndex].deltaQuantity! + newValues.deltaQuantity ?? 0,
        }
        newArticle.locations = newLocations
      }
      // if there is no deltaQ it means that I have changed attributes of that card so I have to create a new article with only one location
      else {
        newArticle.locations = [
          {
            _id: '',
            userId: '',
            ptArticleId: 0,
            name: newValues.locationName ?? realLocationName,
            quantity: newArticle.quantity,
            deltaQuantity: newValues.deltaQuantity ?? newArticle.quantity,
            totalPickedQuantity: 0,
            totalQuantityWithIssues: 0,
          },
        ]
      }
    }

    return newArticle
  }

  const replaceInWorkingCurrentStockACardThatHasBeenGeneratedFromPreviousUpdates = (
    newWorkingCurrentStock: MkmProductInventoryMap,
    cardmarketProductId: number,
    index: number,
    newArticle: InventoryArticle,
    newValues: ChangesContentInterface,
    realLocationName: string
  ) => {
    // checks if in the working current stock there is a card with the same attributes to which to merge
    const matchingArticle = getMatchingWorkingArticle(
      newArticle,
      newWorkingCurrentStock[cardmarketProductId]
    )

    if (
      matchingArticle &&
      matchingArticle.id !== newWorkingCurrentStock[cardmarketProductId][index].id
    ) {
      const findIndexOfMatchingArticle = newWorkingCurrentStock[cardmarketProductId].findIndex(
        (article) => article.id === matchingArticle.id
      )
      const locationToUpdate = newWorkingCurrentStock[cardmarketProductId][
        findIndexOfMatchingArticle
      ].locations?.find((location) => {
        return location.name === newArticle.location
      })

      // increase global quantity
      newWorkingCurrentStock[cardmarketProductId][findIndexOfMatchingArticle].quantity +=
        newArticle.quantity

      // fix quantity per location

      if (locationToUpdate) {
        locationToUpdate.quantity += newArticle.quantity
        if (locationToUpdate.deltaQuantity) locationToUpdate.deltaQuantity += newArticle.quantity
        else locationToUpdate.deltaQuantity = newArticle.quantity
      } else {
        newWorkingCurrentStock[cardmarketProductId][findIndexOfMatchingArticle].locations?.push({
          _id: '',
          userId: '',
          ptArticleId: 0,
          name: newValues.locationName ?? realLocationName,
          quantity: newArticle.quantity,
          deltaQuantity: newValues.deltaQuantity ?? newArticle.quantity,
          totalPickedQuantity: 0,
          totalQuantityWithIssues: 0,
        })
      }

      // to conclude I remove the duplicate that I merged in the matching article
      newWorkingCurrentStock[cardmarketProductId].splice(index, 1)
    }
  }

  const updateCurrentArticleInWorkingStockAccordingNewArticleUpdates = (
    currentArticle: InventoryArticle,
    newWorkingCurrentStock: MkmProductInventoryMap,
    cardmarketProductId: number,
    index: number,
    newValues: ChangesContentInterface,
    newArticle: InventoryArticle
  ) => {
    const currentArticleInWorkingStock = _.cloneDeep(currentArticle)

    currentArticleInWorkingStock.locations?.forEach((location) => {
      if (location.name === newValues.locationName) {
        location.quantity -= newValues.quantity
        if (location.deltaQuantity) location.deltaQuantity -= newValues.quantity
        else location.deltaQuantity = newValues.quantity
      }
    })

    newWorkingCurrentStock[cardmarketProductId][index] = currentArticleInWorkingStock

    // insert into the workingCurrentStock the new card I am creating
    const newWorkingArticle = newArticle.copyWithNewId()
    newWorkingCurrentStock[cardmarketProductId].push(newWorkingArticle)
  }

  const mergeExistingMatchingArticleWithNewArticleIfSameItem = (
    newWorkingCurrentStock: MkmProductInventoryMap,
    cardmarketProductId: number,
    newArticle: InventoryArticle,
    newValues: ChangesContentInterface,
    realLocationName: string
  ) => {
    const matchingArticle = getMatchingWorkingArticle(
      newArticle,
      newWorkingCurrentStock[cardmarketProductId]
    )

    // if a matching article exists I have to arrange the locations and the respective quantities
    if (matchingArticle) {
      // if newValues is newRow I always have to read deltaQ because quantity is 0
      newValues.isNewRow
        ? (matchingArticle.quantity += newValues.deltaQuantity!)
        : (matchingArticle.quantity += newValues.quantity)

      const indexOfSameLocation = matchingArticle.locations?.findIndex((location: Location) => {
        return location.name === (newValues.locationName ?? realLocationName)
      })

      // if there is a location with the same name I have to update the quantities,
      // otherwise it is a new location so I add it to the list
      if (indexOfSameLocation !== -1) {
        const matchingArticleLocation = matchingArticle.locations![indexOfSameLocation!]

        if (!matchingArticleLocation.deltaQuantity) {
          matchingArticleLocation.deltaQuantity = 0
        }

        newValues.isNewRow
          ? (matchingArticleLocation.quantity += newValues.deltaQuantity!)
          : (matchingArticleLocation.quantity += newValues.quantity)

        if (newValues.deltaQuantity) {
          matchingArticleLocation.deltaQuantity! += newValues.deltaQuantity
        } else {
          matchingArticleLocation.deltaQuantity! += newValues.quantity
        }
      } else {
        matchingArticle.locations!.push({
          _id: '',
          userId: '',
          ptArticleId: 0,
          name: newValues.locationName ?? realLocationName,
          quantity: newValues.isNewRow ? newValues.deltaQuantity! : newValues.quantity,
          deltaQuantity: newValues.deltaQuantity ?? newArticle.quantity,
          totalPickedQuantity: 0,
          totalQuantityWithIssues: 0,
        })
      }
    } else {
      const newWorkingArticle = newArticle.copyWithNewId()
      newWorkingCurrentStock[cardmarketProductId].push(newWorkingArticle)
    }
  }

  const handleConfirmLocationsPanel = (changes: ChangesInterface) => {
    if (!currentArticle) {
      return
    }
    const newWorkingCurrentStock = _.cloneDeep(workingCurrentStock)

    // iterate on the changes and apply them to the working current stock
    Object.keys(changes).forEach((key) => {
      let newArticle = _.cloneDeep(currentArticle)
      const newValues = changes[key]

      // if location name contains spaces its necessary remove the panel_location_placeholder and then take all the string
      const parts = key.split('#')
      const realLocationName = parts.slice(1).join('#')

      const cardmarketProductId = currentArticle.card._id

      newArticle = prepareNewArticleWithNewValues(newArticle, newValues, realLocationName)

      // The staged article re-targets the pt_article matching its new SKU. Inherit that
      // pt_article's real reserved quantity if it exists in the inventory; otherwise the
      // new SKU has no DB row yet and reserved is 0. Either way, the source's reserved
      // (carried over by `_.cloneDeep`) is wrong — never use it.
      if (getArticleIdentityKey(newArticle) !== getArticleIdentityKey(currentArticle)) {
        newArticle.ptArticleReservedQuantity = getReservedQuantityForIdentity(
          workingCurrentStock,
          currentArticle.card._id as number,
          getArticleIdentityKey(newArticle)
        )
      }

      // update working current stock
      // case where old card became 0, new card became quantity
      if (newValues.deltaQuantity && newValues.isNewRow === undefined) {
        const index = newWorkingCurrentStock[cardmarketProductId].findIndex((article) => {
          return article.id === currentArticle.id
        })
        const indexOfEditedLocation = currentArticle.locations?.findIndex(
          (location) => location.name === realLocationName
        )
        if (indexOfEditedLocation !== -1)
          newWorkingCurrentStock[cardmarketProductId][index].locations![indexOfEditedLocation!] =
            newArticle.locations![indexOfEditedLocation!]
      } else {
        /*
          I update the starting article to fix the quantities in the locations.
          the starting article is the one present in the newWorkingCurrentStock equal to the currentArticle
          I have to do this when I have changed the attributes and I have to reset the old card to then create the new card,
          or if x cards of y are changing attributes or locations
          */

        const index = newWorkingCurrentStock[cardmarketProductId].findIndex((article) => {
          return article.id === currentArticle.id
        })
        const currentArticleInWorkingStock = newWorkingCurrentStock[cardmarketProductId][index]

        // it means that I am modifying a card that has already been modified a second time
        // 2 cases: I am modifying a card that had been generated, or I am modifying a card that has been changed on the locations
        if (newArticle.newQuantity) {
          /* base case: I'm editing a generated card, so currentArticle has quantity 0 and newQ valued.
          I have to completely replace it in the workingCurrentStock and I don't have to do anything else */

          if (currentArticle.quantity === 0 && currentArticle.newQuantity !== undefined) {
            newWorkingCurrentStock[cardmarketProductId][index] = newArticle
            replaceInWorkingCurrentStockACardThatHasBeenGeneratedFromPreviousUpdates(
              newWorkingCurrentStock,
              cardmarketProductId,
              index,
              newArticle,
              newValues,
              realLocationName
            )
          }
          /* case in which this card has locations immediately in play, then surely currentArticle has a different quantity from
          currentArticleInWorkingStock. this is because currentArticle is drawn from currentStock while currentArticleInWorkingStock
          is drawn from workingCurrentStock */
          // I update the currentArticleInWorkingStock locations according to the card I'm editing (newArticle, or newValues, is the same)
          else {
            if (newValues.isNewRow) {
              mergeExistingMatchingArticleWithNewArticleIfSameItem(
                newWorkingCurrentStock,
                cardmarketProductId,
                newArticle,
                newValues,
                realLocationName
              )
            } else {
              updateCurrentArticleInWorkingStockAccordingNewArticleUpdates(
                currentArticleInWorkingStock,
                newWorkingCurrentStock,
                cardmarketProductId,
                index,
                newValues,
                newArticle
              )
            }
          }
        } else {
          // update the card corresponding to the current article in order to decrease or increase the quantities

          currentArticleInWorkingStock.quantity -= newValues.quantity
          currentArticleInWorkingStock.locations?.forEach((location) => {
            if (location.name === realLocationName) {
              if (!newValues.isNewRow) location.deltaQuantity = -newValues.quantity
              location.quantity -= newValues.quantity
            }
          })

          /*
            I check if there is an article that has the same attributes as newArticle.
            If there is, I can merge them because they are the same card, so I have to appropriately modify
            the locations and the respective quantities and deltaQ
          */

          mergeExistingMatchingArticleWithNewArticleIfSameItem(
            newWorkingCurrentStock,
            cardmarketProductId,
            newArticle,
            newValues,
            realLocationName
          )
        }
      }
    })
    setWorkingCurrentStock(newWorkingCurrentStock)

    handleCloseLocationsPanel()
  }

  const markArticlesAsNotRendered = (stockMap: MkmProductInventoryMap) => {
    Object.keys(stockMap).forEach((cardmarketProductId: any) => {
      const articles = stockMap[cardmarketProductId]
      articles.forEach((article) => {
        article.rendered = false
      })
    })
  }

  const getMatchingWorkingArticle = (
    article: InventoryArticle,
    workingArticles: InventoryArticle[]
  ) => {
    return workingArticles.find((workingArticle) => {
      if (zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE) {
        return areArticlesMatching(article, workingArticle)
      } else if (zoomLevel === INVENTORY_ZOOM_LEVELS.LOCATION) {
        return (
          areArticlesMatching(article, workingArticle) &&
          workingArticle.locations &&
          article.locations &&
          workingArticle.locations[0].name === article.locations[0].name
        )
      }
    })
  }

  const computeNewStockToDisplay = () => {
    const newInitialCurrentStock = _.cloneDeep(initialCurrentStock)

    // in this function we do not change initial or working stocks, we just compute the new stock to display
    let newDisplayInventoryArticles: InventoryArticle[] = []
    // mark all articles as not rendered
    markArticlesAsNotRendered(newInitialCurrentStock)
    markArticlesAsNotRendered(workingCurrentStock)

    // step 1. iterate on the initialCurrentStock and check for matches in the workingCurrentStock
    Object.keys(newInitialCurrentStock).forEach((cardmarketProductId: any) => {
      const initialArticles = newInitialCurrentStock[cardmarketProductId]
      const workingArticles = workingCurrentStock[cardmarketProductId]

      // iterate on the initialArticles and check for matches in the workingArticles
      initialArticles.forEach((initialArticle) => {
        const workingArticle = getMatchingWorkingArticle(initialArticle, workingArticles)
        if (workingArticle) {
          const newArticle = workingArticle.copy()

          // if the locations have delta quantities or the sum of the quantities of the locations is different from the quantity of the article,
          // then this article has undergone modifications so I have to calculate the newQ.
          const newQuantity = workingArticle.locations?.reduce((acc, loc) => acc + loc.quantity, 0)
          const diffQuantity = workingArticle.locations?.reduce((acc, loc) => {
            return acc + loc.quantity - (loc.deltaQuantity || 0)
          }, 0)

          if (diffQuantity !== workingArticle.quantity || newQuantity !== workingArticle.quantity) {
            newArticle.quantity = diffQuantity!
            newArticle.newQuantity = newQuantity
            newArticle.selected = true
          } else {
            // Locations match the saved quantity again — clear any stale `newQuantity`
            // carried from a previous render so the cell renders as unstaged.
            newArticle.newQuantity = undefined
          }

          if (newArticle.newComment) {
            newArticle.selected = true
          }

          workingArticle.rendered = true
          initialArticle.rendered = true
          newArticle.rendered = true

          newDisplayInventoryArticles.push(newArticle)
        } else {
          const newArticle = initialArticle.copy()
          initialArticle.rendered = true
          newArticle.rendered = true
          newDisplayInventoryArticles.push(newArticle)
        }
      })
    })

    // step 2. iterate on the workingCurrentStock: add to the currentStock the cards that are not marked as rendered
    Object.keys(workingCurrentStock).forEach((cardmarketProductId: any) => {
      const workingArticles = workingCurrentStock[cardmarketProductId]
      workingArticles.forEach((workingArticle) => {
        if (!workingArticle.rendered) {
          const newArticle = workingArticle.copy()
          // if the locations have delta quantities or the sum of the quantities of the locations is different from the quantity of the article,
          // then this article has undergone modifications so I have to calculate the newQ.
          const newQuantity = workingArticle.locations?.reduce((acc, loc) => acc + loc.quantity, 0)
          const diffQuantity = workingArticle.locations?.reduce((acc, loc) => {
            return acc + loc.quantity - (loc.deltaQuantity || 0)
          }, 0)

          if (diffQuantity !== workingArticle.quantity || newQuantity !== workingArticle.quantity) {
            newArticle.quantity = diffQuantity!
            newArticle.newQuantity = newQuantity
          } else {
            // Locations match the saved quantity again — clear any stale `newQuantity`
            // carried from a previous render so the cell renders as unstaged.
            newArticle.newQuantity = undefined
          }
          workingArticle.rendered = true
          newArticle.rendered = true
          newArticle.selected = true
          newDisplayInventoryArticles.push(newArticle)
        }
      })
    })
    // sort the new display stock
    newDisplayInventoryArticles = sortInventoryArticles(newDisplayInventoryArticles)
    // update current stock
    setStock(newDisplayInventoryArticles)
  }

  const handleCloseLocationsPanel = () => {
    setShowLocationsPanel(false)
    setCurrentArticle(undefined)
    setCurrentValues({})
    setChanges([])
    setNewRows([])
  }

  const handleConfirmBulkEdit = (type: string, updatedStock: InventoryArticle[]) => {
    const newWorkingCurrentStock = _.cloneDeep(workingCurrentStock)
    updatedStock.forEach((updatedArticle) => {
      const realLocationName = updatedArticle.locations![0].name

      const index = newWorkingCurrentStock[updatedArticle.attributes[3]].findIndex((article) => {
        return article.id === updatedArticle.id
      })
      const indexOfEditedLocation = updatedArticle.locations?.findIndex(
        (location) => location.name === realLocationName
      )
      if (indexOfEditedLocation !== -1)
        newWorkingCurrentStock[updatedArticle.attributes[3]][index].locations![
          indexOfEditedLocation!
        ] = updatedArticle.locations![indexOfEditedLocation!]

      if (type === 'setPriceAndQuantity') {
        newWorkingCurrentStock[updatedArticle.attributes[3]][index].newPrice =
          updatedArticle.newPrice
      }
    })

    setWorkingCurrentStock(newWorkingCurrentStock)
  }

  const applyAutopriceResultsToWorkingCurrentStock = (updatedStock: InventoryArticle[]) => {
    if (parentPage === LISTING_AND_APPRAISAL) {
      // Propagate each priced result to every article sharing the same attribute set, not only the
      // selected one: in listing the same card can appear as several rows and the price is per
      // attribute set, so all matching rows must get priced — mirroring the bulk-edit behaviour in
      // updatePriceOnMultiSelection. Each row keeps its own identity; only price + report change.
      const pricedResults = updatedStock.filter(
        (article): article is PricedInventoryArticle =>
          article instanceof PricedInventoryArticle && article.newPrice !== article.attributes[4]
      )
      if (pricedResults.length === 0) {
        setStock(updatedStock)
        return
      }
      setStock(
        updatedStock.map((article) => {
          const pricedResult = pricedResults.find((priced) => areArticlesMatching(priced, article))
          if (!pricedResult) return article
          const priced = new PricedInventoryArticle({
            ...article,
            failedAutopricing: pricedResult.failedAutopricing,
            newPrice: pricedResult.newPrice,
            priceReport: pricedResult.priceReport,
          })
          priced.selected = true
          return priced
        })
      )
      return
    }

    // Collect one priced result per ptProductId (globally unique) and track which cardmarket
    // products are affected. At LOCATION zoom each location is a separate article (unique id via
    // copyWithNewId) but locations of the same product share a ptProductId and the price is per
    // product, so every location row must get priced — mirroring the bulk-edit behaviour in
    // updatePriceOnMultiSelection. This lets us touch only the affected products and scan each once.
    const pricedResultByPtProductId = new Map<number, PricedInventoryArticle>()
    const affectedCardmarketProductIds = new Set<number>()
    updatedStock.forEach((article) => {
      if (
        article instanceof PricedInventoryArticle &&
        article.newPrice !== article.attributes[4] &&
        article.ptProductId != null
      ) {
        pricedResultByPtProductId.set(Number(article.ptProductId), article)
        affectedCardmarketProductIds.add(article.card._id)
      }
    })

    // Nothing changed: skip the expensive deep clone of the whole working stock.
    if (pricedResultByPtProductId.size === 0) return

    const newWorkingCurrentStock = _.cloneDeep(workingCurrentStock)
    affectedCardmarketProductIds.forEach((cardmarketProductId) => {
      const cardmarketArticles = newWorkingCurrentStock[cardmarketProductId]
      if (!cardmarketArticles) return
      cardmarketArticles.forEach((oldArticle: InventoryArticle, index: number) => {
        const pricedResult = pricedResultByPtProductId.get(Number(oldArticle.ptProductId))
        if (!pricedResult) return
        // Keep this row's own id/locations; only price + report change. Build a
        // PricedInventoryArticle so the strategy tooltip shows; priceReport is shared (read-only).
        cardmarketArticles[index] = new PricedInventoryArticle({
          ...oldArticle,
          failedAutopricing: pricedResult.failedAutopricing,
          newPrice: pricedResult.newPrice,
          priceReport: pricedResult.priceReport,
        })
        cardmarketArticles[index].selected = true
      })
    })
    setWorkingCurrentStock(newWorkingCurrentStock)
  }

  const handleConfirmLinkToLotByInventory = async (
    selectedLot: PurchaseEvent,
    customLinkData?: LinkArticleToPurchaseEvent
  ) => {
    let articlesToLink: LinkArticleToPurchaseEvent
    if (customLinkData) {
      articlesToLink = customLinkData
    } else {
      articlesToLink = {}
      selectedStock.forEach((article) => {
        const key = article.ptProductId!

        // GROUPED
        if (zoomLevel === INVENTORY_ZOOM_LEVELS.ARTICLE) {
          articlesToLink[key] = {
            quantity: article.quantity,
            withVat: selectedLot.withVat!,
          }
        } else {
          // not grouped: check if key exists. if not, create, if yes, update
          if (!articlesToLink[key]) {
            articlesToLink[key] = {
              quantity: article.quantity,
              withVat: selectedLot.withVat!,
            }
          } else articlesToLink[key].quantity += article.quantity
        }
      })
    }

    try {
      const response = await linkArticlesToPurchaseEvent(selectedLot.id, articlesToLink)

      if (response.success) {
        enqueueSnackbar(t('stockPricing.errors.linkedWithSuccess'), { variant: 'success' })
      }
    } catch (e) {
      console.log(e)
      enqueueSnackbar(t('stockPricing.errors.errorLinkingProducts'), { variant: 'error' })
    }
  }

  const handleConfirmLinkToLotByListing = (selectedLot: PurchaseEvent) => {
    // If the parent provides a custom callback, I delegate to that
    if (onConfirmLinkToLotByListing) {
      onConfirmLinkToLotByListing(selectedLot)
      return
    }

    // fallback: old behavior (only set purchaseEventId)
    setStock((old) =>
      old.map((article) => {
        if (!article.selected) return article
        const newArt = article.copy()
        newArt.purchaseEventId = selectedLot.id
        return newArt
      })
    )
  }

  return (
    <div className="inventory-presenter">
      <Box className="d-flex flex-row w-100">
        {!isMobile && (
          <div className="filter-dock-root">
            <FilterDock
              fetchedAt={fetchedAt}
              openImportFileDialog={openImportFileDialog}
              openExportAllProductsCsvDialog={openExportAllProductsCsvDialog}
              areFiltersTouched={areFiltersTouched}
              sealedActive={false}
              filters={filters}
              loading={false} // $% todo see if this matters
              filtersKey={filtersKey}
              setFiltersKey={setFiltersKey}
              setFilters={setFilters}
              defaultFilters={defaultFilters}
              onAddArticles={allowAddingArticles ? handleAddArticles : undefined}
              refreshStockAction={refreshStockAction}
              extraActions={extraDockActions}
              hideExportButton={hideExportButton}
              showAddCardsButton={parentPage === LISTING_AND_APPRAISAL}
              showFiltersButton={parentPage === LISTING_AND_APPRAISAL}
              isEmptyInventory={currentStock.length === 0}
              parentPage={parentPage}
            />
          </div>
        )}
        <Box className="stock-display" flex={1} minWidth={0}>
          <div className={`actions-presenter-container ${viewOnly ? 'd-none' : 'd-block'}`}>
            <TabBox
              active={true}
              text="articles"
              data-testid={`stock`}
              count={currentStock.length}
              onClick={() => {
                return
              }}
              selectedCount={selectedStock.length}
            >
              {!viewOnly && (
                <ActionsPresenter
                  listingTabsState={listingTabsState}
                  selectAllResults={selectAllResultsCommand}
                  unselectAll={unselectAll}
                  handleDeleteArticle={handleDeleteArticle}
                  clearFailedAutoprice={clearFailedAutoprice}
                  revertArticleEdit={revertArticleEditCommand}
                  updateAttributesOfMultiSelectionArticle={
                    updateAttributesOfMultiSelectionArticleCommand
                  }
                  openAutopriceDialog={openAutopriceDialog}
                  openPublishDialog={openPublishDialog}
                  exportSelectedCsv={exportSelectedCsv}
                  selectedStockExists={!!selectedStock.length}
                  selectedStockLength={selectedStock.length}
                  selectedArticles={selectedStock}
                  editedStockExists={!!editedStock.length}
                  parentPage={parentPage}
                  showButtonRemoveComment={
                    !!selectedStock.some((article) => article.comment || article.newComment)
                  }
                  setOpenCsvBulkEditDialog={setOpenCsvBulkEditDialog}
                  handleConfirmLinkToLotByInventory={handleConfirmLinkToLotByInventory}
                  handleConfirmLinkToLotByListing={handleConfirmLinkToLotByListing}
                  moveSelectedItemsToTab={moveSelectedItemsToTab}
                  moveSelectedItemsToNewTab={moveSelectedItemsToNewTab}
                  editBulkBuyPrice={user.featureFlags?.taxation ? editBulkBuyPrice : undefined}
                  disabled={currentStock.length === 0 || stockLock}
                />
              )}
            </TabBox>
          </div>

          {showDialogForGradedCardsWithoutGrade && (
            <ClosableDialog
              open={showDialogForGradedCardsWithoutGrade!}
              onClose={() => setShowDialogForGradedCardsWithoutGrade!(false)}
            >
              <h3>{t('ptInventory.gradedCards.inventoryTitle')}</h3>
              <p>{t('ptInventory.gradedCards.descriptionOne')}</p>
              <p>{t('ptInventory.gradedCards.toFix')}</p>
              <ul>
                <li>{t('ptInventory.gradedCards.stepOne')}</li>
                <li>{t('ptInventory.gradedCards.stepTwo')}</li>
                <li>{t('ptInventory.gradedCards.stepThree')}</li>
                <li>{t('ptInventory.gradedCards.stepFour')}</li>
              </ul>
            </ClosableDialog>
          )}

          <StockDisplay
            listingTabsState={listingTabsState}
            allListingStocks={allListingStocks}
            onAddListingTab={onAddListingTab}
            onActivateListingTab={onActivateListingTab}
            onCloseListingTab={onCloseListingTab}
            onRenameListingTab={onRenameListingTab}
            onAssociateListingTabToLot={onAssociateListingTabToLot}
            unlinkListingTabFromLot={unlinkListingTabFromLot}
            moveAllStockToTab={moveAllStockToTab}
            filteredStock={filteredStock}
            selectedCount={selectedStock.length}
            selectedStockValue={selectedStockValue}
            refreshStockAction={refreshStockAction}
            editedStockExists={!!editedStock.length}
            filteredStockLength={filteredStock.length}
            stockLength={currentStock.length}
            totalFilteredStockValues={totalFilteredStockValues}
            viewOnly={viewOnly}
            sortRefresh={''}
            emptyStockText={emptyStockText}
            openPriceSuggestDrawer={openPriceSuggestDrawer}
            openReservedQuantityDrawer={openReservedQuantityDrawer}
            selectArticle={selectArticle}
            setStock={setStock}
            revertArticleEdit={revertArticleEdit}
            showLocations={showLocations}
            articleWithChangedPriceExists={filteredStock.some((article) => article.newPrice)}
            areFiltersTouched={areFiltersTouched}
            onDeleteArticle={parentPage === LISTING_AND_APPRAISAL ? handleDeleteArticle : undefined}
            parentPage={parentPage}
          />
        </Box>
        <Drawer
          //TransitionComponent={Transition}
          disableEnforceFocus
          anchor="right"
          open={priceSuggestDrawer.open}
          onClose={closePriceDrawer}
        >
          {priceSuggestDrawer.article && (
            <PriceSuggestPresenter
              article={priceSuggestDrawer.article!}
              onClose={() => setPriceSuggestDrawer({ open: false, article: null })}
              priceArticle={priceArticle}
              view={priceSuggestDrawer.activeView}
              parentPage={parentPage}
              totalQuantity={priceSuggestDrawer.totalQuantity}
              refreshStockAction={refreshStockAction}
            />
          )}
        </Drawer>

        <Drawer
          onClose={() => setOpenMobileFilterDock(false)}
          anchor="left"
          open={openMobileFilterDock}
          className="drawer-filter-dock-mobile"
          style={{ width: isPhone ? '60vw' : width / 3 }} // width must be the same in FilterDock component
        >
          <FilterDock
            fetchedAt={fetchedAt}
            openImportFileDialog={openImportFileDialog}
            openExportAllProductsCsvDialog={openExportAllProductsCsvDialog}
            areFiltersTouched={areFiltersTouched}
            sealedActive={false}
            filters={filters}
            loading={false} // $% todo see if this matters
            filtersKey={filtersKey}
            setFiltersKey={setFiltersKey}
            setFilters={setFilters}
            defaultFilters={defaultFilters}
            onAddArticles={allowAddingArticles ? handleAddArticles : undefined}
            refreshStockAction={refreshStockAction}
            extraActions={extraDockActions}
            hideExportButton={hideExportButton}
            showAddCardsButton={parentPage === LISTING_AND_APPRAISAL}
            showFiltersButton={parentPage === LISTING_AND_APPRAISAL}
            isEmptyInventory={currentStock.length === 0}
            parentPage={parentPage}
          />
        </Drawer>

        {[STOCK_DISPLAY, BACKOFFICE_USER_STOCK].includes(parentPage) && (
          <Drawer
            disableEnforceFocus
            anchor="right"
            open={reservedQuantityDrawer.open}
            onClose={closeReservedDrawer}
          >
            {reservedQuantityDrawer.article && (
              <ReservedQuantityReportDrawer
                article={reservedQuantityDrawer.article!}
                onClose={() => setReservedQuantityDrawer({ open: false, article: null })}
              />
            )}
          </Drawer>
        )}

        <ClosableDialog
          maxWidth={'lg'}
          open={autopriceDialogOpen}
          onClose={(_event, reason) => {
            if (reason !== 'backdropClick') {
              cancellationToken.cancel()
              setAutopriceDialogOpen(false)
            }
          }}
        >
          <div className="d-flex flex-row align-items-center">
            <h3 className="me-2">{t('autopriceArticles.title')}</h3>

            <CustomTooltip
              data-testid={'info-autoprice-articles-dialog'}
              title={
                <Box>
                  <Typography gutterBottom variant="body2" data-testid="autoprice-help-tooltip">
                    {t('autopriceArticles.help1')}
                  </Typography>
                  <Typography gutterBottom variant="body2">
                    {t('autopriceArticles.help2')}
                  </Typography>
                  <Typography gutterBottom variant="body2">
                    {t('autopriceArticles.help3')}
                  </Typography>
                  <Typography variant="body2">
                    For more information, check out{' '}
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href="https://support.tcgpowertools.com/support/kb/categories"
                      data-testid={'pricing-settings-our-doc'}
                    >
                      our Knowledge Base
                    </a>
                  </Typography>
                </Box>
              }
            >
              <i className="fa-regular fa-circle-question primary-color fs-5"></i>
            </CustomTooltip>
          </div>
          <AutopricePresenter
            articles={currentStock}
            articlesToPrice={selectedStock.filter((a) => !a.graded.isGraded)}
            applyAutopriceResultsToWorkingCurrentStock={applyAutopriceResultsToWorkingCurrentStock}
            cancellationToken={cancellationToken}
            closeDialog={() => setAutopriceDialogOpen(false)}
            showFailedAutopricingCards
            parentPage={parentPage}
          />
        </ClosableDialog>
        <Drawer
          disableEnforceFocus
          anchor="right"
          open={pricingSettingsDialogOpen}
          onClose={() => setPricingSettingsDialogOpen(false)}
        >
          <Box padding={3} paddingBottom={0} position="relative">
            <DialogTitle disableTypography id="form-dialog-title">
              <h3>
                {t('pricingSettings.title')}
                <HelpButton
                  tooltip={
                    <Typography variant="body2">
                      For more information, check out{' '}
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href="https://support.tcgpowertools.com/support/kb/categories"
                      >
                        our Knowledge Base
                      </a>
                    </Typography>
                  }
                />
              </h3>
            </DialogTitle>
            <Box position="absolute" top="5px" right="5px">
              <IconButton
                data-testid="close-drawer-btn"
                onClick={() => setPricingSettingsDialogOpen(false)}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box width="1100px" maxWidth="90vw" margin="0 auto">
              <PricingSettings />
            </Box>
          </Box>
        </Drawer>

        <ExportAllProductsCsvDialog
          title={t('csv.exportAllProducts', { gameName: activeGame.name })}
          content={<ExportAllProductsCsv />}
        />
        <ClosableDialog
          maxWidth="xl"
          fullWidth={true}
          onClose={() => {
            setImportFileDialogOpen(false)
          }}
          open={importFileDialogOpen}
        >
          <Box minWidth={{ xs: 0, md: '780px' }} margin={{ xs: 1, sm: 3 }}>
            <ImportFileCsv
              enableOverwritePricesFromStock={parentPage === LISTING_AND_APPRAISAL}
              handleResult={(inventory) => {
                //this whole block takes care of creating/adding to the stock everything that has been listed via csv
                if (parentPage === LISTING_AND_APPRAISAL) {
                  const singles: InventoryArticle[] = []
                  const sealed: InventoryArticle[] = []
                  inventory.map((article) => {
                    const locations = [
                      {
                        _id: '',
                        userId: '',
                        ptArticleId: 0,
                        name: article.location ?? '',
                        quantity: article.quantity,
                        deltaQuantity: article.quantity,
                        totalPickedQuantity: 0,
                        totalQuantityWithIssues: 0,
                      },
                    ]
                    article.locations = locations

                    if (isSingle(article.card)) {
                      singles.push(
                        new InventoryArticle({
                          ...article,
                          taxationScheme: user.defaultTaxationSchema.singles,
                          marginTaxationQuantity:
                            user.defaultTaxationSchema.singles === TAXATION_SCHEME.MARGIN
                              ? article.quantity
                              : 0,
                          standardTaxationQuantity:
                            user.defaultTaxationSchema.singles === TAXATION_SCHEME.STANDARD
                              ? article.quantity
                              : 0,
                        })
                      )
                    } else
                      sealed.push(
                        new InventoryArticle({
                          ...article,
                          taxationScheme: user.defaultTaxationSchema.sealed,
                          marginTaxationQuantity:
                            user.defaultTaxationSchema.sealed === TAXATION_SCHEME.MARGIN
                              ? article.quantity
                              : 0,
                          standardTaxationQuantity:
                            user.defaultTaxationSchema.sealed === TAXATION_SCHEME.STANDARD
                              ? article.quantity
                              : 0,
                        })
                      )
                  })
                  // the concatenation order is irrelevant because it is resurrected anyway afterwards
                  const newStock = {
                    stock: stock.concat(singles),
                    sealedStock: sealedStock.concat(sealed),
                    fetchedAt: new Date().toISOString(),
                  }

                  setStockState(newStock as FetchedStockInterface)
                } else {
                  const newInventory = inventory.map((article) => {
                    const locations = [
                      {
                        _id: '',
                        userId: '',
                        ptArticleId: 0,
                        name: article.location ?? '',
                        quantity: article.quantity,
                        deltaQuantity: article.quantity,
                        totalPickedQuantity: 0,
                        totalQuantityWithIssues: 0,
                      },
                    ]
                    article.locations = locations
                    return new InventoryArticle(article)
                  })
                  setStock([...newInventory, ...currentStock])
                }
              }}
              closeExternalDialog={setImportFileDialogOpen}
            />
          </Box>
        </ClosableDialog>
      </Box>
      <Snackbar
        open={!!selectedArticlesHiddenByFilters}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        style={isBelowXl ? { bottom: 90 } : undefined}
      >
        <Alert severity="warning" variant="filled">
          {t('selectedArticlesHiddenByFilters', { count: selectedArticlesHiddenByFilters })}
        </Alert>
      </Snackbar>
      {currentArticle && (
        <LocationsPanel
          open={showLocationsPanel}
          activeGame={activeGame}
          article={currentArticle}
          inventoryMap={workingCurrentStock}
          handleConfirm={handleConfirmLocationsPanel}
          handleClose={handleCloseLocationsPanel}
        />
      )}
      {openCsvBulkEditDialog && (
        <CsvBulkEditDialog
          handleClose={() => {
            setOpenCsvBulkEditDialog(false)
          }}
          activeGame={activeGame}
          confirmUpdates={handleConfirmBulkEdit}
          open={openCsvBulkEditDialog}
          startingStock={_.cloneDeep(currentStock)}
        />
      )}
    </div>
  )
}
